<script setup>
import { computed } from 'vue';

const props = defineProps({
    subject: { type: String, default: '' },
    previewText: { type: String, default: '' },
    blocks: { type: Array, default: () => [] },
    mode: { type: String, required: true },
    isSendingTest: { type: Boolean, default: false },
    showSendTestButton: { type: Boolean, default: true },
});

const emit = defineEmits(['update:subject', 'update:previewText', 'update:blocks', 'edit-image-block', 'send-test']);

const hasContent = computed(() => props.blocks.length > 0);
const isEmailImageBlock = (block) => block?.type === 'email-image-with-link' || block?.type === 'email-image';
const getImageBlockEntryId = (block) => block?.content?.mediaBankEntryId || block?.content?.media_bank_entry_id || null;
const getImagePreviewUrl = (block) => {
    const entryId = getImageBlockEntryId(block);

    return entryId ? route('mediaBank.entries.show', entryId) : null;
};

const updateBlockContent = (index, field, value) => {
    const updated = [...props.blocks];
    updated[index] = {
        ...updated[index],
        content: { ...updated[index].content, [field]: value },
    };
    emit('update:blocks', updated);
};

const moveBlock = (index, direction) => {
    const updated = [...props.blocks];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    emit('update:blocks', updated);
};

const removeBlock = (index) => {
    const updated = props.blocks.filter((_, i) => i !== index);
    emit('update:blocks', updated);
};
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Preview header -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700">
                <mf-translate domainkey="email-ai:preview.title"></mf-translate>
            </h3>
            <button-secondary
                v-if="showSendTestButton && hasContent"
                @click="emit('send-test')"
                :disabled="isSendingTest"
                size="sm"
            >
                <span v-if="isSendingTest">
                    <mf-translate domainkey="email-ai:preview.sending"></mf-translate>
                </span>
                <span v-else>
                    <mf-translate domainkey="email-ai:preview.sendTest"></mf-translate>
                </span>
            </button-secondary>
        </div>

        <!-- Preview content -->
        <div class="flex-1 overflow-y-auto p-6">
            <div v-if="!hasContent" class="flex items-center justify-center h-full text-gray-400 text-sm">
                <div class="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1">
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <polyline points="22,4 12,13 2,4" />
                    </svg>
                    <p><mf-translate domainkey="email-ai:preview.empty"></mf-translate></p>
                </div>
            </div>

            <div v-else class="max-w-2xl mx-auto">
                <!-- Subject line -->
                <div class="mb-4">
                    <label class="block text-xs font-medium text-gray-500 mb-1">
                        <mf-translate domainkey="email-ai:preview.subject"></mf-translate>
                    </label>
                    <input
                        :value="subject"
                        @input="emit('update:subject', $event.target.value)"
                        class="w-full px-3 py-2 text-sm font-semibold border border-gray-200 rounded-lg focus:ring-2 focus:border-transparent"
                        :class="mode === 'newsletter' ? 'focus:ring-aiAgentNewsletterSpecialist' : 'focus:ring-aiAgentAutomationExpert'"
                    />
                </div>

                <!-- Preview text -->
                <div class="mb-6">
                    <label class="block text-xs font-medium text-gray-500 mb-1">
                        <mf-translate domainkey="email-ai:preview.previewText"></mf-translate>
                    </label>
                    <input
                        :value="previewText"
                        @input="emit('update:previewText', $event.target.value)"
                        class="w-full px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg focus:ring-2 focus:border-transparent"
                        :class="mode === 'newsletter' ? 'focus:ring-aiAgentNewsletterSpecialist' : 'focus:ring-aiAgentAutomationExpert'"
                    />
                </div>

                <!-- Email body mockup -->
                <div class="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden whitespace-pre-wrap">
                    <div
                        v-for="(block, index) in blocks"
                        :key="block.uuid || index"
                        class="group relative border-b border-gray-100 last:border-b-0"
                    >
                        <!-- Block controls -->
                        <div class="absolute right-2 top-2 hidden group-hover:flex items-center gap-1 z-10">
                            <button-base
                                v-if="isEmailImageBlock(block)"
                                @click="emit('edit-image-block', index)"
                                style-type="default"
                                size="xs"
                                class="!px-1 !py-1"
                                title="Edit image"
                            >
                                <template #icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M17.414 2.586a2 2 0 010 2.828l-8.5 8.5a2 2 0 01-.878.497l-3 1a1 1 0 01-1.265-1.265l1-3a2 2 0 01.497-.878l8.5-8.5a2 2 0 012.828 0z" />
                                    </svg>
                                </template>
                            </button-base>
                            <button-base
                                v-if="index > 0"
                                @click="moveBlock(index, -1)"
                                style-type="default"
                                size="xs"
                                class="!px-1 !py-1"
                                title="Move up"
                            >
                                <template #icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                                    </svg>
                                </template>
                            </button-base>
                            <button-base
                                v-if="index < blocks.length - 1"
                                @click="moveBlock(index, 1)"
                                style-type="default"
                                size="xs"
                                class="!px-1 !py-1"
                                title="Move down"
                            >
                                <template #icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </template>
                            </button-base>
                            <button-base
                                @click="removeBlock(index)"
                                style-type="default"
                                size="xs"
                                class="!px-1 !py-1 !text-red-500"
                                title="Remove"
                            >
                                <template #icon>
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </template>
                            </button-base>
                        </div>

                        <!-- email-header -->
                        <div v-if="block.type === 'email-header'" class="p-6">
                            <textarea
                                :value="block.content?.title || ''"
                                @input="updateBlockContent(index, 'title', $event.target.value)"
                                class="w-full text-2xl font-bold text-gray-900 border-0 border-b border-transparent hover:border-gray-200 focus:ring-0 bg-transparent p-0"
                                :class="mode === 'newsletter' ? 'focus:border-aiAgentNewsletterSpecialist' : 'focus:border-aiAgentAutomationExpert'"
                            />
                        </div>

                        <!-- email-text -->
                        <div v-else-if="block.type === 'email-text'" class="p-6" :style="{ textAlign: block.content?.alignment || 'left' }">
                            <div
                                contenteditable="true"
                                v-html="block.content?.text || ''"
                                @blur="updateBlockContent(index, 'text', $event.target.innerHTML)"
                                class="email-preview-rich-text w-full text-sm text-gray-700 leading-relaxed outline-none min-h-[3em] border-b border-transparent hover:border-gray-200"
                                :class="mode === 'newsletter' ? 'focus:border-aiAgentNewsletterSpecialist' : 'focus:border-aiAgentAutomationExpert'"
                                :style="{ textAlign: block.content?.alignment || 'left' }"
                            ></div>
                        </div>

                        <!-- email-button -->
                        <div v-else-if="block.type === 'email-button'" class="p-6 text-center">
                            <div class="inline-block">
                                <input
                                    :value="block.content?.title || ''"
                                    @input="updateBlockContent(index, 'title', $event.target.value)"
                                    class="px-0 py-2 text-sm text-gray-900 rounded-md text-center border-0 focus:ring-2 focus:ring-offset-2"
                                    :class="mode === 'newsletter' ? 'bg-wlPrimary text-wlPrimaryContrast focus:ring-aiAgentNewsletterSpecialist' : 'bg-wlPrimary text-wlPrimaryContrast focus:ring-aiAgentAutomationExpert'"
                                />
                            </div>
                            <div class="mt-1 flex space-x-px items-end justify-center">
                                <input-label class="pb-px" :label="_mft('blockEditor:link.label')+':'"></input-label>
                                <input
                                    :value="block.content?.url || ''"
                                    @input="updateBlockContent(index, 'url', $event.target.value)"
                                    class="text-sm text-gray-400 border-0 border-b border-transparent hover:border-gray-200 focus:ring-0 bg-transparent p-0 px-1"
                                    :class="mode === 'newsletter' ? 'focus:border-aiAgentNewsletterSpecialist' : 'focus:border-aiAgentAutomationExpert'"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>

                        <!-- email-image-with-link -->
                        <div v-else-if="isEmailImageBlock(block)" class="p-6">
                            <button
                                type="button"
                                class="group/image block w-full text-left"
                                @click="emit('edit-image-block', index)"
                            >
                                <div class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 transition-colors" :class="mode === 'newsletter' ? 'group-hover/image:border-aiAgentNewsletterSpecialist' : 'group-hover/image:border-aiAgentAutomationExpert'">
                                    <media-bank-entries-display
                                        v-if="getImagePreviewUrl(block)"
                                        :data-url="getImagePreviewUrl(block)"
                                        class="block w-full"
                                    ></media-bank-entries-display>
                                    <div
                                        v-else
                                        class="flex min-h-[180px] items-center justify-center px-4 py-10 text-sm text-gray-400"
                                    >
                                        <mf-translate domainkey="blockEditor:selectImageFromMediaLibrary.label"></mf-translate>
                                    </div>
                                </div>
                            </button>

                        </div>

                        <!-- email-panel -->
                        <div v-else-if="block.type === 'email-panel'" class="p-6">
                            <div class="bg-gray-100 p-4 rounded-r">
                                <div
                                    contenteditable="true"
                                    v-html="block.content?.text || ''"
                                    @blur="updateBlockContent(index, 'text', $event.target.innerHTML)"
                                    class="email-preview-rich-text w-full text-sm text-gray-700 outline-none min-h-[2em] text-center"
                                ></div>
                            </div>
                        </div>

                        <!-- email-subcopy -->
                        <div v-else-if="block.type === 'email-subcopy'" class="px-6 py-3">
                            <div
                                contenteditable="true"
                                v-html="block.content?.text || ''"
                                @blur="updateBlockContent(index, 'text', $event.target.innerHTML)"
                                class="email-preview-rich-text w-full text-xs text-gray-400 outline-none min-h-[1.5em]"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.email-preview-rich-text :deep(ul) {
    list-style-type: disc;
    padding-left: 1.25rem;
    margin: 0.75rem 0;
}

.email-preview-rich-text :deep(ol) {
    list-style-type: decimal;
    padding-left: 1.25rem;
    margin: 0.75rem 0;
}

.email-preview-rich-text :deep(li) {
    display: list-item;
    margin: 0.25rem 0;
}
</style>
